import { X, UploadSimple } from "@phosphor-icons/react";
import { useState } from "react";
import "./PromotionModal.css";
import { toast, Toaster } from "react-hot-toast";
import axiosInstance from "../../../api/axiosInstance";
import { uploadImageToServer } from "../../../api/uploadImage";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import { AUDIENCE_OPTIONS, OFFERABLE_SERVICES } from "./promoUtils";
import { toHotelISO } from "../../../api/hotelTime";

dayjs.extend(utc);

const allowedImageTypes = ["image/jpeg", "image/png", "image/jpg"];

// Create a new manual promotion (new promotions-engine contract).
export default function PromotionsModal({ onClose, onCreated }) {
    const [formData, setFormData] = useState({
        title: "",
        text: "",
        target_audience: "",
        service: "",
        offer_type: "",
        offer_value: "",
        min_order_amount: "",
        start_date: "",
        end_date: "",
        image_key: "",
        filePreview: "",
        file: null,
    });
    const [uploading, setUploading] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [errors, setErrors] = useState({});

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({ ...prev, [name]: value }));
        setErrors((prev) => ({ ...prev, [name]: "" }));
    };

    const validateForm = () => {
        const newErrors = {};
        if (!formData.image_key) newErrors.image = "Image is required";
        if (!formData.title.trim()) newErrors.title = "Title is required";
        if (!formData.text.trim()) newErrors.text = "Message is required";
        if (!formData.target_audience.trim()) newErrors.target_audience = "Please select target audience";
        if (!formData.service.trim()) newErrors.service = "Please select a service";
        if (formData.offer_type && !formData.offer_value) newErrors.offer_value = "Enter an offer value";
        if (formData.start_date && formData.end_date && dayjs(formData.end_date).isBefore(dayjs(formData.start_date))) {
            newErrors.end_date = "End date must be after start date";
        }
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    // Upload image to S3 via presigned URL; returns the stored key.
    const uploadImage = async (file) => {
        const response = await axiosInstance.get(
            `/api/v1/images/upload-presigned-url?file_name=${file.name}`
        );
        const { key, url } = response.data;
        const uploaded = await uploadImageToServer(file, url);
        if (!uploaded) {
            throw new Error("Image upload failed");
        }
        return key;
    };

    const handleFileChange = async (e) => {
        const file = e.target.files[0];
        if (!file || !allowedImageTypes.includes(file.type)) {
            toast.error("Please upload a valid .jpg, .jpeg or .png image");
            return;
        }
        try {
            setUploading(true);
            const imageKey = await uploadImage(file);
            setFormData((prev) => ({
                ...prev,
                image_key: imageKey,
                file,
                filePreview: URL.createObjectURL(file),
            }));
            setErrors((prev) => ({ ...prev, image: "" }));
            toast.success("Image uploaded successfully");
        } catch (error) {
            console.error("Error uploading image:", error);
            toast.error("Error occurred while uploading image. Please re-upload.");
        } finally {
            setUploading(false);
        }
    };

    const handleSubmit = async () => {
        if (!validateForm()) return;
        try {
            setSubmitting(true);
            const hotelId = localStorage.getItem("hotelId");
            const payload = {
                title: formData.title,
                text: formData.text,
                target_audience: formData.target_audience,
                photo_url: formData.image_key || "",
                service: formData.service || "",
                offer_type: formData.offer_type || "",
                offer_value: formData.offer_value ? Number(formData.offer_value) : 0,
                min_order_amount: formData.min_order_amount ? Number(formData.min_order_amount) : 0,
            };
            if (formData.start_date) payload.start_date = toHotelISO(formData.start_date);
            if (formData.end_date) payload.end_date = toHotelISO(formData.end_date);

            await axiosInstance.post(`/api/v1/hotels/${hotelId}/promotions`, payload);
            toast.success("Promotion created");
            if (onCreated) onCreated();
            onClose();
        } catch (error) {
            toast.error(
                `Could not create promotion: ${error?.response?.data?.error || error?.response?.data?.message || "Unknown error"}`
            );
            console.error(error);
        } finally {
            setSubmitting(false);
        }
    };

    const inputClass =
        "border border-gray-300 rounded-md px-4 py-2 focus:outline-none focus:ring-2 focus:ring-[#ff7378]";

    return (
        <>
            <div className="fixed top-0 left-0 h-screen w-screen bg-black bg-opacity-60 flex items-center justify-center z-[1001]">
                <div className="promotionModal w-[42%] min-w-[380px] max-h-[90vh] popup-content bg-white p-6 rounded-2xl text-sm flex flex-col gap-2 overflow-y-auto">
                    <div className="flex items-center justify-between">
                        <h2 className="text-xl font-semibold">Add New Promotion</h2>
                        <button className="bg-[#F2F7FF] p-1.5 border-[1.5px] rounded-md" onClick={onClose}>
                            <X weight="bold" size={18} />
                        </button>
                    </div>

                    <div className="mt-4 flex flex-col gap-4 w-full">
                        {/* Image upload */}
                        <div className="flex flex-col gap-2">
                            <h4 className="font-semibold">Image</h4>
                            <label className="flex w-full cursor-pointer bg-gray-100 hover:bg-gray-200 px-4 py-5 rounded-md border border-gray-300 text-center">
                                <div className="flex items-center gap-2 mx-auto">
                                    <UploadSimple size={20} />
                                    <span>{formData.image_key ? "Image Uploaded" : "Upload Image"}</span>
                                </div>
                                <input type="file" className="hidden" onChange={handleFileChange} />
                            </label>
                            {formData.filePreview && (
                                <div className="w-full h-[150px] border border-gray-300 rounded-md overflow-hidden">
                                    <img src={formData.filePreview} alt="Preview" className="w-full h-full object-contain" />
                                </div>
                            )}
                            {uploading && <p className="text-[#ff7378]">Uploading image...</p>}
                            {errors.image && <p className="text-red-500">{errors.image}</p>}
                        </div>

                        {/* Title */}
                        <div className="flex flex-col gap-2">
                            <h4 className="font-semibold">Title</h4>
                            <input
                                type="text"
                                placeholder="Title"
                                name="title"
                                className={inputClass}
                                value={formData.title}
                                onChange={handleChange}
                            />
                            {errors.title && <p className="text-red-500">{errors.title}</p>}
                        </div>

                        {/* Message */}
                        <div className="flex flex-col gap-2">
                            <h4 className="font-semibold">Message to be sent</h4>
                            <textarea
                                placeholder="Message to be sent...."
                                rows={4}
                                name="text"
                                value={formData.text}
                                onChange={handleChange}
                                className={`${inputClass} resize-none`}
                            />
                            {errors.text && <p className="text-red-500">{errors.text}</p>}
                        </div>

                        {/* Service */}
                        <div className="flex flex-col gap-2">
                            <h4 className="font-semibold">Service</h4>
                            <select name="service" className={inputClass} value={formData.service} onChange={handleChange}>
                                <option value="">Select Service</option>
                                {OFFERABLE_SERVICES.map((s) => (
                                    <option key={s} value={s}>
                                        {s}
                                    </option>
                                ))}
                            </select>
                            {errors.service && <p className="text-red-500">{errors.service}</p>}
                        </div>

                        {/* Offer */}
                        <div className="grid grid-cols-2 gap-3">
                            <div className="flex flex-col gap-2">
                                <h4 className="font-semibold">Offer Type</h4>
                                <select name="offer_type" className={inputClass} value={formData.offer_type} onChange={handleChange}>
                                    <option value="">No Offer</option>
                                    <option value="PERCENT_OFF">Percent Off (%)</option>
                                    <option value="AMOUNT_OFF">Amount Off (₹)</option>
                                </select>
                            </div>
                            <div className="flex flex-col gap-2">
                                <h4 className="font-semibold">Offer Value</h4>
                                <input
                                    type="number"
                                    min="0"
                                    placeholder={formData.offer_type === "PERCENT_OFF" ? "e.g. 20" : "e.g. 500"}
                                    name="offer_value"
                                    className={inputClass}
                                    value={formData.offer_value}
                                    onChange={handleChange}
                                    disabled={!formData.offer_type}
                                />
                                {errors.offer_value && <p className="text-red-500">{errors.offer_value}</p>}
                            </div>
                        </div>

                        {/* Min order */}
                        <div className="flex flex-col gap-2">
                            <h4 className="font-semibold">Minimum Order Amount (₹) (optional)</h4>
                            <input
                                type="number"
                                min="0"
                                placeholder="0"
                                name="min_order_amount"
                                className={inputClass}
                                value={formData.min_order_amount}
                                onChange={handleChange}
                            />
                        </div>

                        {/* Audience */}
                        <div className="flex flex-col gap-2">
                            <h4 className="font-semibold">Target Audience</h4>
                            <div className="grid grid-cols-2 gap-2">
                                {AUDIENCE_OPTIONS.map((a) => {
                                    const selected = formData.target_audience === a.value;
                                    return (
                                        <button
                                            key={a.value}
                                            type="button"
                                            onClick={() =>
                                                handleChange({ target: { name: "target_audience", value: a.value } })
                                            }
                                            className={`flex items-center gap-2 text-left px-3.5 py-2.5 rounded-lg border-[1.5px] transition ${
                                                selected
                                                    ? "border-[#ff7378] bg-[#FFF0F0] text-[#ff7378] font-semibold"
                                                    : "border-gray-200 bg-white text-gray-500 hover:border-gray-300"
                                            }`}
                                        >
                                            {selected && (
                                                <span className="w-1.5 h-1.5 rounded-full bg-[#ff7378] shrink-0" />
                                            )}
                                            <span className="text-[13px]">{a.label}</span>
                                            {a.value === "ALFRED" && (
                                                <span className="ml-auto text-[10px] font-bold bg-[#f5f3ff] text-[#7c3aed] rounded px-1.5 py-0.5">
                                                    AI
                                                </span>
                                            )}
                                        </button>
                                    );
                                })}
                            </div>
                            {errors.target_audience && <p className="text-red-500">{errors.target_audience}</p>}
                        </div>

                        {/* Dates */}
                        <div className="grid grid-cols-2 gap-3">
                            <div className="flex flex-col gap-2">
                                <h4 className="font-semibold">Start Date (optional)</h4>
                                <input
                                    type="datetime-local"
                                    className={inputClass}
                                    name="start_date"
                                    value={formData.start_date}
                                    onChange={handleChange}
                                />
                            </div>
                            <div className="flex flex-col gap-2">
                                <h4 className="font-semibold">End Date (optional)</h4>
                                <input
                                    type="datetime-local"
                                    className={inputClass}
                                    name="end_date"
                                    value={formData.end_date}
                                    onChange={handleChange}
                                />
                                {errors.end_date && <p className="text-red-500">{errors.end_date}</p>}
                            </div>
                        </div>
                    </div>

                    {/* Submit */}
                    <div className="w-full mt-4 text-center">
                        <button
                            className="bg-[#ff7378] w-full py-3 rounded-md text-white font-bold shadow-md hover:bg-[#f75f64] transition disabled:opacity-60"
                            onClick={handleSubmit}
                            disabled={submitting || uploading}
                        >
                            {submitting ? "Adding..." : "Add the Promotion"}
                        </button>
                    </div>
                </div>
            </div>

            <Toaster position="bottom-right" reverseOrder={false} style={{ marginBottom: "25%" }} />
        </>
    );
}
